// Next.js 커스텀 이미지 로더 — Vercel·EKS 양 플랫폼 공용(next.config images.loader).
//
// 배경: next/image 의 기본 최적화(/_next/image)는 sharp(아키텍처 종속 네이티브 모듈)를
// 요구한다. 셀프호스팅 컨테이너에 sharp 를 넣는 대신, ZEP 이 이미 운영 중인
// cdn-static 리사이즈 람다(`?w=<px>&f=webp` 온더플라이 변형)에 위임한다.
// → 파드 CPU/메모리 부담 0, Dockerfile 불변, /_next/image DoS·OOM 리스크 소멸.
// CloudFront 가 `?w=` 변형을 캐시하므로 람다도 대부분 우회된다.
//
// 양 플랫폼 통일 이유: 오리진마다 이미지 URL 스킴이 다르면 CloudFront 가 캐시한 HTML 이
// 컷오버 후 없는 /_next/image 를 가리켜 404 난다. 로더를 양쪽에 두면 두 오리진이 같은
// cdn-static URL 을 생성 → 캐시된 HTML 이 오리진 무관하게 유효(카나리/컷오버 안전).
//
// 화이트리스트는 libs/common/utils resized-image-url.ts 의 isResizableCdnHost 와
// 동일하게 유지한다(다른 CloudFront 배포/서명 URL 에 `?w=` 를 붙여 403·깨짐 방지).

const RESIZABLE_HOST_PATTERN = /^cdn-static[\w-]*\.zep\.us$/;
const RESIZABLE_CLOUDFRONT_HOST = 'd2kljb6v9f27on.cloudfront.net';

/**
 * @param {{ src: string, width: number }} params
 * @returns {string}
 */
export default function cdnStaticImageLoader({ src, width }) {
  let url;
  try {
    url = new URL(src);
  } catch {
    // 상대경로(로컬 /assets/*) 등 파싱 불가 → 원본 통과(최적화 안 함)
    return src;
  }

  const isResizable =
    RESIZABLE_HOST_PATTERN.test(url.hostname) ||
    url.hostname === RESIZABLE_CLOUDFRONT_HOST;
  if (!isResizable) {
    // 리사이즈 불가 호스트(zepeto 에셋·weegloo 등) → **원본 문자열 그대로** 반환.
    // url.toString() 을 쓰면 불필요한 정규화(퍼센트 인코딩 등)가 섞일 수 있다.
    return src;
  }

  // SVG 는 리사이즈하지 않는다.
  //  - 벡터라 해상도 독립적이다. `?w=` 로 특정 폭에 고정할 이유가 없고, `f=webp` 는
  //    래스터로 변환해 오히려 확대 시 품질이 깨진다.
  //  - 특히 `sprite.svg#icon` 같은 fragment 참조는 래스터가 되는 순간 브라우저가
  //    `#icon` 요소를 선택할 수 없어 **스프라이트 전체가 표시된다**.
  // fragment 유무와 무관하게 통과시키는 게 단순하고 안전하다.
  if (url.pathname.toLowerCase().endsWith('.svg')) {
    return src;
  }

  // 문자열 결합 대신 URL 객체를 쓰는 이유: fragment 가 있으면 결합 결과가
  // `...png#top?w=..` 이 되어 쿼리가 fragment 안으로 들어간다. 브라우저는 `#` 뒤를
  // 서버로 보내지 않으므로 **리사이즈가 통째로 유실**된다.
  // searchParams 는 fragment 를 보존하며, 기존 w/f 가 있어도 중복 없이 덮어쓴다.
  url.searchParams.set('w', String(width));
  url.searchParams.set('f', 'webp');
  return url.toString();
}
